import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Button, Switch, Tooltip } from 'rizzui';
import { toast } from 'sonner';
import { ArrowBack } from '@mui/icons-material';
import DeletePopover from '../../../components/buttons/DeletePopover';
import { Loader } from '../../../components/loader/SpinnerLogo';
import TabNav, { MenuItem } from "../../../components/navigation/TabNav";
import { usePrivilege } from '../../../components/priviledge/PriviledgeProvider';
import useFetch from '../../../hooks/useFetch';
import useHandleErrors from '../../../hooks/useHandleErrors';
import PageHeader from "../../../layout/shared/page-header";
import { setSelectedView } from '../../../redux/callSlice';
import { RootState } from '../../../redux/store';
import { menuRoutes } from "../../../router/menu";
import { CallsApiResponse } from '../../../type/entities/calls-type';
import { CallsService } from '../../../services/calls/callsService';

const menuItems: MenuItem[] = [
    {
        label: 'Información',
        path: menuRoutes.calls.profile.info,
        permission: {
            group: 'calls',
            action: 'admin_calls',
        },
    },
    {
        label: 'Grabaciones',
        path: menuRoutes.calls.profile.records,
        permission: {
            group: 'calls',
            action: 'admin_calls',
        },
    }
];

type CallProfileLocationState = {
    fromContract?: boolean;
    returnToContractPath?: string;
    fromClient?: boolean;
    returnToClientPath?: string;
};

export default function CallProfileLayout({ isLoading, children }: { isLoading?: boolean, children: React.ReactNode }) {

    const { selectedView } = useSelector((state: RootState) => state.callProfile);
    const { id = '' } = useParams<{ id: string }>();
    const location = useLocation();
    const { userCan } = usePrivilege();
    const navigate = useNavigate();
    const { handleErrors } = useHandleErrors();
    const service = new CallsService();
    const profileLocationState = (location.state as CallProfileLocationState | null) ?? null;
    const showBackToContractCalls = Boolean(profileLocationState?.fromContract && profileLocationState?.returnToContractPath);
    const showBackToClientCalls = Boolean(profileLocationState?.fromClient && profileLocationState?.returnToClientPath);

    const [loading, setLoading] = useState<boolean>(false);
    const [rowId, setRowId] = useState<string>('');
    const [openCallModal, setOpenCallModal] = useState<boolean>(false);

    const [data, loadinCall, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getCall(id);
        return response.getResponseData() as CallsApiResponse;
    }, [id]));

    const pageHeader = {
        title: `Información de llamada`,
        breadcrumb: [
            { name: 'Ventas' },
            { name: 'Gestión de contratos' },
            { name: 'Llamadas', href: menuRoutes.calls.path },
            { name: 'Información de llamada' },
        ],
    };

    const handleDelete = async (id: string) => {
        try {
            const response = await (await service.deleteCall(id)).getResponseData();
            if (response.success) {
                navigate(menuRoutes.calls.path);
                toast.success('Llamada eliminada correctamente');
            } else {
                handleErrors(response);
            }
        } catch (error) {
            console.log(error);
        }
    };

    useEffect(() => {
        navigate(`${menuRoutes.calls.path}/${id}/profile${selectedView}`, { state: location.state });
    }, [id, location.state, navigate, selectedView]);

    return (
        <>
            <PageHeader title={pageHeader.title} breadcrumb={pageHeader.breadcrumb}>
                <div className='flex flex-row flex-nowrap gap-3 mt-3 md:mt-0'>
                    {showBackToContractCalls && (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(profileLocationState?.returnToContractPath || menuRoutes.contracts.path)}
                        >
                            <ArrowBack /> Volver a llamadas del contrato
                        </Button>
                    )}

                    {showBackToClientCalls && (
                        <Button
                            variant={'outline'}
                            size={'md'}
                            className={'bg-primary text-white hover:bg-transparent hover:text-secondary hover:border-muted'}
                            onClick={() => navigate(profileLocationState?.returnToClientPath || menuRoutes.clients.path)}
                        >
                            <ArrowBack /> Volver a llamadas del cliente
                        </Button>
                    )}

                    {userCan('delete_calls', 'calls') && (
                        <DeletePopover
                            title={`Eliminar llamada`}
                            description={`¿Estás seguro de que deseas eliminar la llamada?`}
                            size={'md'}
                            onDelete={() => handleDelete(id)}
                            actionIconClassName='h-auto w-10'
                        />
                    )}
                </div>
            </PageHeader>

            <TabNav menuItems={menuItems} setSelectedView={setSelectedView} />

            {isLoading ? <Loader height='60vh' /> : children}
        </>
    );
}
